import { useCallback, useContext, useState } from 'react'
import { addMonths } from 'date-fns/addMonths'
import { addDays } from 'date-fns/addDays'
import { Button, cn, DatePicker, TravelerSelection } from '@pcln/horizon'
import type { ITravelerInfo } from '@pcln/horizon'
import type { DateRange } from 'react-day-picker'
import { format } from 'date-fns/format'
import { useForm } from 'react-hook-form'
import pclnExperimentation from '@pcln/experimentation'
import Sticky from 'react-stickynode'
import AutocompleteWrapper from '@/components/AutocompleteWrapper/AutocompleteWrapper'
import StickyEffect from '@/components/StickyEffect'
import type { GA4PageNameType } from '@/ga4types'
import type { LOCATION_SEARCH_TYPE, TravelerState } from '@/types'
import { SearchFormContext } from '@/context/SearchFormContext'
import { yupResolver } from '@hookform/resolvers/yup'
import useTabGroup from '@/hooks/useTabGroup'
import logSearchFormAnalytics from '@/shared-utils/log-search-form-analytics'
import fireMaxRoomsEvent from '@/components/TravelerSelection/ga4'
import HorizonBundleAndSave from '@/components/SearchFormExperiment/utils/HorizonBundleAndSave'
import { TRAVELER_SELECTION_COLLISION_AVOIDANCE } from '@/components/SearchForm/constants'
import useSeti from '@/hooks/useSeti'
import MultiHotelForm from './HorizonMultiHotelForm/HorizonMultiHotelForm'
import {
  getFormContainerClass,
  getGridContainerClass,
  getInputClass
} from './HorizonHotelForm.variants'
import type {
  HOTEL_FORM_SUBMIT_TYPE,
  HOTEL_FORM_STATE_TYPE,
  SEM_SEARCH_TYPE,
  SelectedTopFilters
} from '../types'
import useHotelFormInitialValues from './useHotelFormInitialValues'
import useHotelFormSubmitValues from './useHotelFormSubmitValues'
import {
  INFANT_CAPACITY_LIMIT,
  isInfantLimitReachedPackages,
  searchFormSchema
} from '../SearchForm/validation'
import FormTypeRadios from './HorizonFormTypeRadio'
import { groupDealsURL } from '../SearchForm/utils'
import HorizonSelectedFilterChipBar from './HorizonSelectedFilterChipBar'

type FormType = {
  onSubmit: (data: HOTEL_FORM_SUBMIT_TYPE, pageName: GA4PageNameType) => void
  // promoDeal?: PromoDealType
  setLocation?: (city?: LOCATION_SEARCH_TYPE) => void
  showHotelTypeButtons?: boolean
  pageName?: 'homepage' | 'landing'
}

export default function HorizonHotelForm({
  onSubmit,
  setLocation = () => null,
  showHotelTypeButtons = false,
  pageName = 'homepage'
}: FormType) {
  const [hotelType, setHotelType] = useState('single')
  const [addFlights, setAddFlights] = useState(false)
  const [isStickyInEffect, setIsStickyInEffect] = useState(false)
  const { registerTabElement, focusNextElement } = useTabGroup()
  const { updateFormValues } = useContext(SearchFormContext)
  const { initialValues, isMobile, cguid, SEMSearch, SEMSEARCH } =
    useHotelFormInitialValues()

  const {
    clearErrors,
    control,
    setValue,
    handleSubmit,
    watch,
    trigger,
    formState: { errors }
  } = useForm<HOTEL_FORM_STATE_TYPE>({
    defaultValues: initialValues,
    mode: 'onSubmit',
    resolver: yupResolver(searchFormSchema),
    reValidateMode: 'onChange'
  })

  const {
    watchTripType,
    watchStartLocation,
    watchEndLocation,
    watchStartDate,
    watchEndDate,
    watchTravelers
  } = useHotelFormSubmitValues({
    control,
    watch,
    clearErrors,
    trigger,
    initialValues,
    setLocation,
    updateFormValues
  })

  const [bundleSelections, setBundleSelections] = useState<{
    [key: string]: boolean
  }>({
    flights: false,
    car: false
  })

  const enableStickyEffect =
    !isMobile && pageName === 'homepage' && hotelType === 'single'

  const handleStickyEffectStateChange = ({ status }: Sticky.Status) => {
    if (status === Sticky.STATUS_FIXED && !isStickyInEffect) {
      // Only allow sticky for single hotel forms
      if (enableStickyEffect) {
        setIsStickyInEffect(true)
      }
    } else if (isStickyInEffect) {
      setIsStickyInEffect(false)
    }
  }
  const [maxSelectableDate, setMaxSelectableDate] = useState<Date | undefined>()
  const currentDate = new Date()
  const [defaultMonth, setDefaultMonth] = useState<Date>(currentDate)

  const onCalendarChange = useCallback(
    (dates: DateRange | undefined): void => {
      if (dates?.from) {
        const startDate = format(dates.from, 'yyyyMMdd')
        setValue('startDate', startDate, { shouldDirty: true })
        setDefaultMonth(dates.from)
      }

      if (dates?.to) {
        const endDate = format(dates.to, 'yyyyMMdd')
        setValue('endDate', endDate, { shouldDirty: true })
      }

      if (dates?.from && dates?.to === undefined) {
        setValue('endDate', '', { shouldDirty: true })
        setMaxSelectableDate(addDays(dates.from, 28))
      } else {
        setMaxSelectableDate(undefined)
      }
    },
    [setValue]
  )

  const isPartnersURL = useSeti('HP_LP_HOTELS_TEN_ROOMS', false) === 'VARIANT'

  const hotelPlannerLink = groupDealsURL({
    location: watchEndLocation ? watchEndLocation.itemName : '',
    startDate: watchStartDate,
    endDate: watchEndDate,
    isPartnersURL
  })
  const hotelPlannerLinkAttrsProp = {
    href: hotelPlannerLink,
    target: '_blank'
  }

  const onItemSelect = useCallback(
    (
      item: LOCATION_SEARCH_TYPE | null,
      searchKey: 'startLocation' | 'endLocation'
    ) => {
      setValue(searchKey, item)
      if (!isMobile && !!item && typeof focusNextElement === 'function') {
        focusNextElement(true)
      } else if (
        isMobile &&
        !!item &&
        document.activeElement instanceof HTMLElement
      ) {
        document.activeElement.blur()
      }
    },
    [isMobile, setValue, focusNextElement]
  )

  const [selectedFilterChipData, setSelectedFilterChipData] =
    useState<SelectedTopFilters>(() => ({
      amenities: new Set(initialValues.selectedTopFilters.amenities),
      neighborhoods: new Set(initialValues.selectedTopFilters.neighborhoods),
      hotelStarLevel: new Set(initialValues.selectedTopFilters.hotelStarLevel),
      guestRating: new Set(initialValues.selectedTopFilters.guestRating),
      propertyTypes: new Set(initialValues.selectedTopFilters.propertyTypes)
    }))

  const hasSelectedFilters = Object.values(selectedFilterChipData).some(
    value => value.size > 0
  )

  const showSelectedFilters = watchTripType === 'STAY' && hasSelectedFilters

  const handleSubmitHelper = handleSubmit((data: HOTEL_FORM_STATE_TYPE) => {
    // Handle single hotel form submission
    let userLatitude: number | undefined
    let userLongitude: number | undefined
    if (
      data.endLocation?.type === 'GEO' &&
      data.endLocation?.lat &&
      data.endLocation?.lon
    ) {
      userLatitude = data.endLocation.lat
      userLongitude = data.endLocation.lon
    }
    const formValues = {
      ...data,
      ...(SEMSearch && { SEMSearch: SEMSearch as SEM_SEARCH_TYPE }),
      ...(SEMSEARCH && { SEMSEARCH: SEMSEARCH as SEM_SEARCH_TYPE }),
      mapView: false,
      selectedTopFilters: selectedFilterChipData,
      useCurrentLocation: data.endLocation?.type === 'GEO',
      ...(userLatitude &&
        userLongitude && {
          userLatitude,
          userLongitude
        })
    }
    onSubmit(formValues, pageName)
    logSearchFormAnalytics(cguid, pageName, 'hotels', isMobile)
  })

  const handleTravelerChange = (travelerInfo: ITravelerInfo): void => {
    setValue('travelers', travelerInfo as TravelerState)
  }

  const handleBundleSelectionChange = useCallback(
    (updatedTripType: string, checked: boolean, optionValue: string) => {
      setBundleSelections(prev => ({
        ...prev,
        [optionValue]: checked
      }))
      setValue('tripType', updatedTripType)
      if (optionValue === 'FLY') {
        setAddFlights(checked)
      }
    },
    [setValue]
  )

  const formClasses = getFormContainerClass(
    addFlights,
    isStickyInEffect,
    showSelectedFilters
  )
  const gridContainerClass = getGridContainerClass(
    addFlights,
    isStickyInEffect,
    showSelectedFilters
  )
  const numberOfMonthsDisplay = watchTripType === 'STAY' ? 13 : 12

  return (
    <div className="w-auto">
      {showHotelTypeButtons && (
        <FormTypeRadios formType={hotelType} onChange={setHotelType} />
      )}
      <StickyEffect
        enabled={enableStickyEffect}
        isStickyInEffect={isStickyInEffect}
        handleStickyEffectStateChange={handleStickyEffectStateChange}
        innerZ={isMobile ? 0 : 3}
      >
        {hotelType === 'multi' && showHotelTypeButtons ? (
          <MultiHotelForm pageName={pageName} />
        ) : (
          <div className={formClasses}>
            <form
              className="w-auto"
              aria-label="horizon-search-form"
              onSubmit={handleSubmitHelper}
            >
              <div className={gridContainerClass}>
                {addFlights && (
                  <div className={getInputClass('flight')}>
                    <AutocompleteWrapper
                      label="Where from?"
                      isMobile={isMobile}
                      errorMessage={errors.startLocation?.message}
                      searchKey="startLocation"
                      typeAheadEndpoint="flights"
                      initialSelectedItem={
                        watchStartLocation as LOCATION_SEARCH_TYPE | undefined
                      }
                      onItemSelect={itemSelected => {
                        onItemSelect(itemSelected, 'startLocation')
                      }}
                      ref={(elem: HTMLInputElement) => {
                        registerTabElement(elem)
                      }}
                    />
                  </div>
                )}
                <div className={getInputClass('location')}>
                  <AutocompleteWrapper
                    label="Where to?"
                    isMobile={isMobile}
                    showCurrentLocation
                    errorMessage={errors.endLocation?.message}
                    searchKey="endLocation"
                    initialSelectedItem={
                      watchEndLocation as LOCATION_SEARCH_TYPE | undefined
                    }
                    typeAheadEndpoint="hotels"
                    onItemSelect={itemSelected => {
                      onItemSelect(itemSelected, 'endLocation')
                    }}
                    ref={(elem: HTMLInputElement) => {
                      registerTabElement(elem)
                    }}
                    selectedFilterChipData={selectedFilterChipData}
                    setSelectedFilterChipData={setSelectedFilterChipData}
                    showFilterChips={watchTripType === 'STAY'}
                  />
                </div>

                <div className={getInputClass('datePicker')}>
                  <DatePicker
                    isMobile={isMobile}
                    inputRef={registerTabElement}
                    errorMessage={(() => {
                      if (errors.startDate) {
                        return errors.startDate.message
                      }
                      if (errors.endDate) {
                        return errors.endDate.message
                      }
                    })()}
                    className="w-full"
                    title="Hotels: Check-in - Check-out"
                    label="Check-in - Check-out"
                    mode="range"
                    numberOfMonths={isMobile ? numberOfMonthsDisplay : 2}
                    handleCalendarChange={onCalendarChange}
                    selected={{
                      from: currentDate,
                      to: addDays(currentDate, 1)
                    }}
                    disabled={{
                      before: currentDate,
                      after: maxSelectableDate
                    }}
                    startMonth={currentDate}
                    endMonth={addMonths(currentDate, numberOfMonthsDisplay - 1)}
                    defaultMonth={defaultMonth}
                    allowSameDayInRange={false}
                  />
                </div>
                <div className={getInputClass('travelers')}>
                  <TravelerSelection
                    isMobile={isMobile}
                    isHotel={!watchTripType.includes('FLY')}
                    initialValues={initialValues.travelers}
                    roomsEnabled
                    maxRooms={watchTripType === 'STAY' ? 9 : 4}
                    maxChildren={watchTripType === 'STAY' ? 8 : undefined}
                    maxTravelers={watchTripType === 'STAY' ? 36 : 8}
                    fireMaxRoomsOnClick={() => {
                      pclnExperimentation.fireImpression(
                        'HP_LP_HOTELS_TEN_ROOMS'
                      )
                      window.open(hotelPlannerLinkAttrsProp.href, '_blank')
                      fireMaxRoomsEvent(pageName, 'hotel')
                    }}
                    ageSelectEnabled
                    tooltipErrorMessageId="travelerSelectionError"
                    errorMessage={
                      errors?.travelers?.children?.message ||
                      errors?.travelers?.rooms?.message ||
                      (isInfantLimitReachedPackages(
                        watchTravelers?.adults,
                        watchTravelers?.childrenAges,
                        watchTripType
                      ) &&
                        INFANT_CAPACITY_LIMIT) ||
                      ''
                    }
                    onChange={(values: ITravelerInfo) => {
                      handleTravelerChange(values)
                    }}
                    collisionAvoidance={TRAVELER_SELECTION_COLLISION_AVOIDANCE}
                  />
                </div>
                {showSelectedFilters && (
                  <div className={getInputClass('selectedFilters')}>
                    <HorizonSelectedFilterChipBar
                      selectedFilterChipData={selectedFilterChipData}
                      setSelectedFilterChipData={setSelectedFilterChipData}
                    />
                  </div>
                )}
                <div
                  className={cn(
                    getInputClass('packageDeals'),
                    isStickyInEffect ? 'mb-4 mt-0' : 'my-2',
                    'flex flex-wrap'
                  )}
                >
                  <HorizonBundleAndSave
                    checkedValues={bundleSelections}
                    onSelectionChange={handleBundleSelectionChange}
                    tripType={watchTripType}
                    productType="hotels"
                  />
                </div>
                <Button
                  buttonType="submit"
                  type="primary"
                  size="lg"
                  className={cn(
                    isMobile
                      ? 'fixed bottom-4 right-4 left-4'
                      : getInputClass('submit')
                  )}
                >
                  Search
                </Button>
              </div>
            </form>
          </div>
        )}
      </StickyEffect>
    </div>
  )
}
